import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Sprout, Wheat, Droplets, Sun, Tractor, CloudRain, ShieldCheck, Apple } from 'lucide-react';

const SustainableAgriculture: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Verdant Fields Academy";
    
    return (
        <div className="min-h-screen bg-[#fcf8f2] font-serif text-[#4a4036] selection:bg-[#8f9e7d] selection:text-white">
            {/* Top Bar */}
            <div className="bg-[#4a4036] text-[#e0d6c8] py-2 px-8 text-center font-sans text-xs font-medium tracking-wide">
                Enrolling now for the Fall Harvest Semester.
            </div>

            {/* Navbar */}
            <nav className="sticky top-0 z-50 bg-[#fcf8f2]/95 backdrop-blur border-b border-[#e0d6c8]">
                <div className="max-w-7xl mx-auto px-8 h-24 flex justify-between items-center">
                    <div className="flex items-center gap-2">
                        <div className="bg-[#8f9e7d] w-10 h-10 rounded-full flex items-center justify-center text-white">
                            <Wheat size={20} />
                        </div>
                        <span className="font-bold text-xl text-[#2c2620]">{schoolName}</span>
                    </div>

                    <div className="hidden md:flex gap-10 font-sans text-xs font-bold text-[#8c8275] uppercase tracking-widest">
                        <a href="#" className="hover:text-[#4a4036] transition-colors">Our Farms</a>
                        <a href="#" className="hover:text-[#4a4036] transition-colors">Permaculture</a>
                        <a href="#" className="hover:text-[#4a4036] transition-colors">Research</a>
                        <a href="#" className="hover:text-[#4a4036] transition-colors">Market</a>
                    </div>

                    <Button variant="outline" className="rounded-full border-[#8f9e7d] text-[#5e6b50] hover:bg-[#8f9e7d] hover:text-white font-sans font-bold uppercase tracking-widest text-[10px] h-9 px-6 transition-all">
                        Visit Campus
                    </Button>
                </div>
            </nav>

            {/* Hero Section */}
            <header className="relative pt-20 pb-32 px-8 overflow-hidden">
                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                    
                    {/* Text Content */}
                    <div className="relative z-10">
                         <div className="inline-flex items-center gap-2 text-[#8f9e7d] mb-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
                             <Sprout size={18} />
                             <span className="font-sans text-xs font-bold uppercase tracking-widest">Regenerative Farming</span>
                         </div>
                         
                         <h1 className="text-6xl md:text-7xl font-light text-[#2c2620] mb-8 leading-tight">
                             Feeding the <br/>
                             Future, <span className="italic text-[#8f9e7d]">Naturally.</span>
                         </h1>
                         
                         <p className="font-sans text-[#6b6156] text-lg leading-relaxed max-w-md mb-12">
                             Learn the science of sustainable agriculture. From soil health to crop rotation, we teach methods that heal the earth while producing abundance.
                         </p>
                         
                         <div className="flex gap-4">
                             <Button className="rounded-full bg-[#5e6b50] hover:bg-[#4a543f] text-white font-sans font-bold uppercase tracking-widest text-xs h-12 px-8 shadow-xl shadow-[#8f9e7d]/20">
                                 View Courses
                             </Button>
                             <div className="flex items-center gap-3 px-6 py-3 bg-white rounded-full shadow-sm border border-[#e0d6c8]">
                                 <Sun size={18} className="text-yellow-500" />
                                 <span className="font-sans text-xs font-bold text-[#6b6156]">Field Conditions: Perfect</span>
                             </div>
                         </div>
                    </div>

                    {/* Image Composition */}
                    <div className="relative h-[600px] w-full">
                        <div className="absolute top-0 right-0 w-3/4 h-3/4 bg-[#e8e0d5] rounded-tl-[100px] rounded-br-[100px] overflow-hidden">
                             <img 
                                src="https://images.unsplash.com/photo-1500382017468-9049fed747ef?q=80&w=2232&auto=format&fit=crop" 
                                alt="Wheat Field" 
                                className="w-full h-full object-cover"
                             />
                        </div>
                        <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-white p-4 shadow-xl rounded-tl-[50px] rounded-br-[50px]">
                            <img 
                                src="https://images.unsplash.com/photo-1530836369250-ef72a3f5cda8?q=80&w=2070&auto=format&fit=crop" 
                                alt="Hands with Soil" 
                                className="w-full h-full object-cover rounded-tl-[40px] rounded-br-[40px]"
                            />
                        </div>
                        
                        {/* Floating Badge */}
                         <div className="absolute top-[60%] right-[10%] bg-[#fcf8f2] p-6 rounded-full shadow-lg border border-[#e0d6c8] flex flex-col items-center justify-center w-32 h-32 text-center z-10">
                             <span className="block text-3xl font-bold text-[#5e6b50]">100%</span>
                             <span className="block font-sans text-[10px] uppercase font-bold text-[#8c8275]">Organic</span>
                         </div>
                    </div>
                </div>
            </header>

            {/* Principles / Grid */}
            <section className="bg-white py-24 px-8 border-t border-[#e0d6c8]">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-light text-[#2c2620] mb-4">Core Principles</h2>
                        <div className="w-16 h-1 bg-[#8f9e7d] mx-auto rounded-full"></div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                        {[
                            { icon: Tractor, title: "Agroecology", desc: "Farming that works with nature, not against it." },
                            { icon: Droplets, title: "Water Systems", desc: "Efficient irrigation and rainwater harvesting." },
                            { icon: CloudRain, title: "Climate Resilience", desc: "Adapting crops for a changing planet." },
                            { icon: Apple, title: "Food Security", desc: "Ensuring access to nutritious food for all." },
                        ].map((item, i) => (
                            <div key={i} className="bg-[#fcf8f2] p-8 rounded-3xl text-center hover:bg-[#f0ebe3] transition-colors cursor-pointer">
                                <item.icon className="mx-auto text-[#8f9e7d] mb-6" size={32} strokeWidth={1.5} />
                                <h3 className="text-xl font-medium text-[#2c2620] mb-3">{item.title}</h3>
                                <p className="font-sans text-sm text-[#6b6156] leading-relaxed">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

             {/* Footer */}
            <footer className="bg-[#4a4036] text-[#e0d6c8] py-20 px-8">
                 <div className="max-w-7xl mx-auto">
                     <div className="flex flex-col md:flex-row justify-between items-start gap-12 mb-16">
                         <div>
                             <h2 className="text-3xl font-light text-white mb-6">{schoolName}</h2>
                             <p className="font-sans text-sm max-w-sm leading-relaxed opacity-80">
                                 Cultivating minds and soil since 1974. We are committed to a sustainable future through education and practice.
                             </p>
                         </div>
                         <div className="flex gap-4">
                             <ShieldCheck size={48} strokeWidth={1} className="opacity-50" />
                             <div className="font-sans text-xs">
                                 <span className="block font-bold uppercase tracking-widest text-[#8f9e7d] mb-1">Certified</span>
                                 <span className="block opacity-80">Organic Institution</span>
                             </div>
                         </div>
                     </div>
                     
                     <div className="border-t border-[#5e544a] pt-8 flex flex-col md:flex-row justify-between items-center gap-6 font-sans text-xs uppercase tracking-widest opacity-60">
                         <div className="flex gap-8">
                             <a href="#" className="hover:text-white transition-colors">Campus Map</a>
                             <a href="#" className="hover:text-white transition-colors">CSA Program</a>
                             <a href="#" className="hover:text-white transition-colors">Contact</a>
                         </div>
                         <p>© 2026 {schoolName}.</p>
                     </div>
                 </div>
            </footer>
        </div>
    );
};

export default SustainableAgriculture;
